/**
 * Type-ahead over GET /api/locations. Transient UI state (query, results)
 * lives here; the PICK lands in the store via onPick — the DOM is never the
 * source of truth for the selection. Results render the label verbatim
 * (specs click getByText(label, { exact: true })).
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { getOr } from '../lib/api.js';
import { areaForRoutingKey, GAZETTEER } from '../../shared/gazetteer.js';
import { matchTown } from '../../shared/town-match.js';

type LocationRow = { id: string; label: string; inGrid: boolean; region: string | null };

/** A town offered as a browse chip (T660 M9, P0-27). Shaped to take an
 *  `OptionsResponse['airportPickups']` entry as-is. */
export type BrowsableTown = { locationId: string; label: string; region?: string | null };

/** Towns of one area, in the order the server sorted them (ORDER BY label). */
type TownGroup = { key: string; label: string; towns: BrowsableTown[] };

/**
 * Human label for a region SLUG, derived rather than looked up: 'north-wicklow'
 * → 'North Wicklow'. A derivation, not a map, on purpose — registry-first
 * forbids the client hardcoding a domain enumeration, and a hardcoded list
 * would render a region added later as a raw slug (or drop it).
 */
function areaLabel(region: string): string {
  return region
    .split('-')
    .filter((w) => w !== '')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** The bucket for a town whose area is unknown — see `groupByArea`. */
const UNKNOWN_AREA_KEY = '';
const UNKNOWN_AREA_LABEL = 'Other';

/**
 * Group towns by area for the browse block.
 *
 * A town with NO region is bucketed under `Other`, never dropped: an
 * operator-added airport rate has no `locations` row to join a region from
 * (`parseRateEntryBody` validates its name for length only), so dropping the
 * unmatched case would silently hide exactly the town Helen just added. The
 * plan records this as erratum 39a.
 *
 * Areas sort alphabetically, with `Other` pinned last — it is a fallback
 * bucket, not an area, so it should not sort into the middle of real ones.
 */
function groupByArea(towns: readonly BrowsableTown[]): TownGroup[] {
  const byKey = new Map<string, TownGroup>();
  for (const t of towns) {
    const key = t.region ?? UNKNOWN_AREA_KEY;
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: key === UNKNOWN_AREA_KEY ? UNKNOWN_AREA_LABEL : areaLabel(key), towns: [] };
      byKey.set(key, group);
    }
    group.towns.push(t);
  }
  return [...byKey.values()].sort((a, b) => {
    if (a.key === UNKNOWN_AREA_KEY) return 1;
    if (b.key === UNKNOWN_AREA_KEY) return -1;
    return a.label.localeCompare(b.label);
  });
}

/** Stable, collision-free id fragment for one search's browse block. */
function slugForIds(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'loc';
}

export type LocationSearchProps = {
  placeholder: string;
  /**
   * K5-6 / A2-pricelists-1 — the VISIBLE label for the filter input.
   *
   * ⛔ IT MUST NOT RESTATE THE PLACEHOLDER, which is the whole lesson of
   * `II-admin-F21`: the rate list's fix gave its box a label reading exactly
   * what the placeholder read, so nothing was mislabelled and one of the two
   * said nothing new. The placeholder here names the VALUE ('Pickup location',
   * and the net-locked 'Type to filter locations...'); the label names the
   * ACTION.
   *
   * Optional so the three spec files that mount this component directly keep
   * compiling, but every real mount passes its own: two of them share the
   * point-to-point screen, and two textboxes with one accessible name is the
   * defect this fix exists to remove, not a smaller version of it.
   */
  label?: string;
  selectedLabel: string | null;
  /** Pre-typed text (inquiry prefill whose location had no single exact
   *  match) — Helen confirms the pick; the UI never auto-picks it. */
  initialQuery?: string | null;
  /** T385 item 5 — routing key from the inquiry's address-end Eircode. When
   *  it maps to a declared area (gazetteer ROUTING_KEY_AREAS): a display
   *  hint line, area towns ranked first, and — on an EMPTY query — the area
   *  towns offered as starting suggestions. Suggestion-grade only: never a
   *  filter on typed queries, never an auto-pick (A63 spans 6+ towns). */
  routingKey?: string | null;
  onPick: (id: string, label: string) => void;
  onClear: () => void;
  /** T373 M4 — when set, an escape control appears in the zero-result state so a
   *  genuinely-unlisted place can be quoted by hand with its true name. Receives
   *  what Helen typed. Absent for callers with no hand-quote path (e.g. golf uses
   *  tiles, not this search). */
  onEscape?: (typedText: string) => void;
  /** T385 item 6 — fired at the TERMINAL moments of a gazetteer miss: the
   *  escape control is taken, or the search is abandoned (unmount with a
   *  SETTLED zero-result query and no pick). Never fired per keystroke, never
   *  while the latest fetch is unresolved, and at most once per normalized
   *  query per mount — the dedupe lives here because location_misses has no
   *  unique constraint to lean on. */
  onMiss?: (typedText: string) => void;
  /**
   * T660 M9 (P0-27) — the priced towns, offered as browsable chips while
   * NOTHING IS TYPED. Optional, and absent means today's behaviour exactly:
   * that is what keeps `location-search-hint.spec.tsx` green untouched, since
   * its `:72-84` case asserts an empty query with no routing key renders no
   * button at all.
   *
   * Deliberately sited in this component rather than in `Location.tsx`: the
   * block must vanish the moment Helen types, and `query` is state that lives
   * here. Siting it here also serves the airport screen and BOTH point-to-point
   * ends from one implementation.
   *
   * ⚠️ ONE OBJECT, not two sibling optional props, and the shape is the point.
   * `endLabel` ('Pickup' / 'Drop-off') prefixes each chip's ACCESSIBLE NAME, and
   * that prefix is load-bearing rather than decorative: point-to-point mounts
   * this component twice, and filling the pickup box does not empty the drop-off
   * one — so a bare chip named 'Bray' beside a search RESULT named 'Bray' makes
   * `getByRole('button', { name: 'Bray', exact: true })` match two nodes and
   * fail strict mode. As two optional props the pairing was enforced by this
   * comment alone, so a future caller (M10's reshape moves these screens) could
   * pass the towns without the label and silently reinstate the collision.
   * Bundled, that is unrepresentable. WCAG 2.5.3 holds either way: the
   * accessible name contains the visible text.
   */
  browse?: { towns: readonly BrowsableTown[]; endLabel: string };
};

export function LocationSearch({ placeholder, label = 'Find a place', selectedLabel, initialQuery, routingKey, onPick, onClear, onEscape, onMiss, browse }: LocationSearchProps) {
  const [query, setQuery] = useState(initialQuery ?? '');
  const [results, setResults] = useState<LocationRow[]>([]);
  const latest = useRef(0);
  const area = useMemo(() => areaForRoutingKey(routingKey ?? null), [routingKey]);
  /** The last query whose fetch RESOLVED, with its row count — the "settled"
   *  signal (the zero-result block also renders mid-fetch, and getOr maps a
   *  network blip to [], so results alone cannot distinguish those). */
  const settled = useRef<{ q: string; count: number } | null>(null);
  /** Normalized queries already reported this mount (once-per-query dedupe). */
  const reported = useRef<Set<string>>(new Set());
  /** Live mirrors for the unmount closure (state captured there is stale). */
  const live = useRef({ query: initialQuery ?? '', picked: selectedLabel !== null });
  live.current = { query, picked: selectedLabel !== null };

  const reportMiss = (typed: string) => {
    if (!onMiss) return;
    const key = typed.trim().toLowerCase();
    if (key === '' || reported.current.has(key)) return;
    reported.current.add(key);
    onMiss(typed.trim());
  };

  useEffect(() => {
    if (query.trim() === '' && !area) {
      setResults([]);
      return;
    }
    const seq = ++latest.current;
    // Empty query + a known area: fetch the full list and offer the area's
    // towns as starting suggestions — strictly additive (this state used to
    // render nothing). Typed queries search normally.
    void getOr<LocationRow[]>(`/api/locations?q=${encodeURIComponent(query.trim())}`, []).then(
      (rows) => {
        if (seq !== latest.current) return;
        settled.current = { q: query.trim(), count: rows.length };
        if (query.trim() === '' && area) {
          setResults(rows.filter((r) => area.towns.includes(r.label)));
        } else {
          setResults(rows);
        }
      },
    );
  }, [query, area]);

  // Abandonment (T385 item 6): leaving the screen with a settled zero-result
  // query and no pick is the strongest miss signal there is — and it used to
  // log nothing. Tab/browser-close doesn't reach this (SPA unmount only);
  // accepted limit recorded in the plan.
  useEffect(() => () => {
    const cur = live.current;
    const s = settled.current;
    if (cur.picked) return;
    const q = cur.query.trim();
    if (q !== '' && s !== null && s.q === q && s.count === 0) reportMiss(q);
  }, []);

  // Area towns rank first on typed queries (stable within each group) —
  // ordering only, never removal.
  const ordered = useMemo(() => {
    if (!area || query.trim() === '') return results;
    const inArea = results.filter((r) => area.towns.includes(r.label));
    return [...inArea, ...results.filter((r) => !area.towns.includes(r.label))];
  }, [results, area, query]);

  /**
   * T485 — a near-miss spelling the DB search cannot reach ("Grestones",
   * "Dun Laoghaire"). Offered as an explicitly-labelled SUGGESTION, never an
   * auto-pick: the inference is visibly an inference and the customer's own
   * words stay in the input above it, so Helen can audit before she commits.
   *
   * Gated on `settled` for the same reason reportMiss is: this block also
   * renders mid-fetch, and getOr maps a network blip to [], so an ungated
   * suggestion could appear for a town that actually exists.
   *
   * Deliberately only the FUZZY tier: the deterministic tiers already resolve
   * server-side via the resolver, so anything reaching here is a guess.
   */
  const suggestion = (() => {
    const q = query.trim();
    const s = settled.current;
    if (q === '' || s === null || s.q !== q || s.count !== 0) return null;
    const m = matchTown(q, GAZETTEER);
    if (m.kind !== 'fuzzy') return null;
    // A suggestion identical to what is already typed helps nobody.
    return m.name.toLowerCase() === q.toLowerCase() ? null : m.name;
  })();

  /**
   * The browse block is an EMPTY-QUERY affordance only. Hiding it on the first
   * keystroke is what makes P0-27 affordable: the screen returns to its present
   * two affordances the moment Helen starts typing, and — the reason this is a
   * correctness rule and not a taste one — a chip and a search result can never
   * be on screen together, so no `getByText(label).first()` in the net can
   * silently start matching a chip instead of the result it was written for.
   */
  const browseGroups = useMemo(
    () => (browse && browse.towns.length > 0 ? groupByArea(browse.towns) : []),
    [browse],
  );
  const showBrowse = browseGroups.length > 0 && query.trim() === '';
  const browseIdBase = slugForIds(browse?.endLabel ?? placeholder);
  /** The label/input association, through the file's own id idiom. Keyed off the
   *  PLACEHOLDER rather than the label: the placeholder is what already differs
   *  per mount by construction (the two point-to-point searches sit in one
   *  document), and `slugForIds` guarantees no whitespace. */
  const fieldId = `loc-search-${slugForIds(placeholder)}`;
  // The `: label` arm is unreachable — chips render only inside `showBrowse`,
  // which implies `browse` — and is kept only so this needs no non-null assertion.
  const chipName = (label: string) => (browse ? `${browse.endLabel}: ${label}` : label);

  /**
   * What the results list still has to say once the chips are up.
   *
   * ⚠️ This is a FILTER, not a suppression, and the difference is the whole
   * point. Suppressing the T385 routing-key suggestions outright looked safe —
   * "those towns are already chips" — and is FALSE for the two routing keys
   * whose towns carry no airport rate: **A96** (Dún Laoghaire, Sallynoggin) and
   * **D18** (Cabinteely, Shankill, Sandyford, Cherrywood, Stepaside). Seven
   * towns, none of them ever a chip, so a south-Dublin caller would have had
   * their own area's suggestions replaced by a Wicklow chip list — including
   * `Dún Laoghaire`, which is the very town the requirement's own diacritic
   * argument is built on ("typing `dun` cannot find it").
   *
   * So: drop only the rows a chip ALREADY offers, keep the rest. Matched on
   * `locationId`, the key, never on the label. Off the empty-query path this
   * filter is inert — `showBrowse` is false and `ordered` passes through whole.
   */
  const chipIds = useMemo(
    () => new Set((browse?.towns ?? []).map((t) => t.locationId)),
    [browse],
  );
  const visibleResults = showBrowse ? ordered.filter((r) => !chipIds.has(r.id)) : ordered;

  if (selectedLabel !== null) {
    return (
      <div class="loc-selected">
        <span class="loc-selected-label">{selectedLabel}</span>
        <button type="button" class="btn btn--quiet btn--inline loc-change" onClick={() => { setQuery(''); onClear(); }}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div class="loc-search">
      {/* K5-6: a REAL visible label, not placeholder-only labelling (DESIGN.md
          §3.2; the kiosk's own Login screen was fixed for this in T937). It
          carries `.field-label`, which is both the 20px/600 declaration and the
          thing `style-contract.spec.ts`'s "no unclassed <label>" pin looks for.

          ⛔ `aria-label={placeholder}` IS GONE, deliberately. A `<label for>` IS
          the accessible name, so leaving the old attribute on would have the
          label say one thing on screen and another to a screen reader. The e2e
          net reaches this input with `getByPlaceholder` at five sites, so the
          placeholder (net-locked, verbatim) is what the net grades; the vitest
          sites that queried the old ARIA name are paired in the same change. */}
      <label class="field-label" for={fieldId}>{label}</label>
      <input
        id={fieldId}
        class="login-input"
        type="text"
        placeholder={placeholder}
        autocomplete="off"
        value={query}
        onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
      />
      {area && (
        // Display line only (affordance budget #7): the customer's Eircode
        // area, so Helen knows roughly where they are while she picks.
        <p class="helper-note">Eircode {area.key} · {area.areaLabel}</p>
      )}
      {showBrowse && (
        <div class="town-browse">
          {browseGroups.map((g) => {
            // Both halves through `slugForIds`. `g.key` is a region slug and so
            // is already id-safe today — but it is the one half of this id that
            // comes from the DATABASE, and leaving it raw made "region values are
            // id-safe" an assumption held by nothing. A malformed value would
            // break the aria-labelledby association silently (no throw, no
            // failing test, just a group that stops announcing its area).
            const areaKey = g.key === UNKNOWN_AREA_KEY ? 'other' : slugForIds(g.key);
            const headingId = `${browseIdBase}-area-${areaKey}`;
            return (
              // aria-labelledby rather than aria-label: the area name is already
              // on screen, and duplicating it into the group's own label would
              // have a screen reader announce it twice on entry.
              <div key={g.key} class="town-browse-group" role="group" aria-labelledby={headingId}>
                <p id={headingId} class="town-browse-area">{g.label}</p>
                <div class="chip-row">
                  {g.towns.map((t) => (
                    <button
                      key={t.locationId}
                      type="button"
                      class="btn btn--quiet chip-btn"
                      aria-label={chipName(t.label)}
                      onClick={() => onPick(t.locationId, t.label)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* With the chips up, this holds exactly the T385 area suggestions that
          are NOT already chips — see `visibleResults`. A town is therefore
          offered once, never twice and never zero times. */}
      {visibleResults.length > 0 && (
        <ul class="loc-results">
          {visibleResults.map((r) => (
            <li key={r.id}>
              <button type="button" class="loc-result" onClick={() => onPick(r.id, r.label)}>
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {query.trim() !== '' && results.length === 0 && (
        <div class="helper-note">
          {/* Deliberately does NOT assert the place doesn't exist: getOr swallows
              a fetch failure to [], so "no rows" and "network blip" look the same
              here. It offers a way forward without making a claim. */}
          <p>No matching locations yet. Keep typing, or check the spelling.</p>
          {suggestion && (
            // setQuery, NOT onPick: the match came from the gazetteer by NAME
            // and carries no location_id. Re-running the search renders the
            // real DB row as an ordinary result, which Helen taps through the
            // normal pick path — so the id always comes from the database and
            // this stays a suggestion rather than a selection.
            <button
              type="button"
              class="btn btn--quiet"
              onClick={() => setQuery(suggestion)}
            >
              Did they mean {suggestion}?
            </button>
          )}
          {onEscape && (
            <button
              type="button"
              class="btn btn--quiet loc-escape"
              onClick={() => {
                // The taken escape IS a miss (offered → taken): log it with
                // resolution unresolved — the persist-coupled writer records
                // 'manual' later IF the hand quote actually persists. Only a
                // SETTLED zero-result state may report (mid-fetch renders
                // this block too, and a blip-born miss row would be noise).
                const s = settled.current;
                if (s !== null && s.q === query.trim() && s.count === 0) reportMiss(query);
                onEscape(query.trim());
              }}
            >
              Quote {query.trim()} by hand
            </button>
          )}
        </div>
      )}
    </div>
  );
}
